import { useState, useCallback, useEffect, useRef } from 'react'
import { EnterpriseSummary } from '../clients/kyousou_api_client'
import { getKyousouApiClient } from '../services/network'
import {
  EnterpriseSortType,
  EnterpriseSortTypeValue,
} from '../types/enterprise'
interface SearchParams {
  keyword?: string
  enterpriseCategory?: string
  businessCategory?: string
  location?: string
  companySize?: string
  capital?: string
  sdgsCategories?: string[]
}

export const useEnterpriseSearch = (
  limit = 9,
  // TODO: マッチング率ソートはパフォーマンス改善後に有効化
  // initialSort: EnterpriseSortTypeValue = EnterpriseSortType.MATCHING_RATE,
  initialSort: EnterpriseSortTypeValue = EnterpriseSortType.CREATED_AT,
  initialSearchParams: SearchParams = {}
) => {
  const [enterprises, setEnterprises] = useState<EnterpriseSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [currentSort, setCurrentSort] = useState(initialSort)
  const [currentSearchParams, setCurrentSearchParams] =
    useState<SearchParams>(initialSearchParams)
  const abortControllerRef = useRef<AbortController | null>(null)

  const apiClient = getKyousouApiClient()
  const hasMore = cursor < totalCount

  const search = useCallback(
    async (params: SearchParams, isLoadMore = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }
      // PHPで配列として認識させるため、キー名に[]を付ける
      const sdgsCategoriesParam =
        params.sdgsCategories && params.sdgsCategories.length > 0
          ? { 'sdgsCategories[]': params.sdgsCategories }
          : {}

      try {
        const response = await apiClient.enterprise.enterpriseList({
          keyword: params.keyword,
          enterpriseCategory: params.enterpriseCategory,
          businessCategory: params.businessCategory,
          address: params.location,
          companySize: params.companySize,
          capital: params.capital,
          ...sdgsCategoriesParam,
          cursor: isLoadMore ? cursor : 0,
          sort: currentSort,
          limit,
        })

        const newEnterprises = response.data.enterprises || []
        setTotalCount(response.data.total || 0)

        if (!abortControllerRef.current?.signal.aborted) {
          setEnterprises((prevEnterprises) => {
            if (!isLoadMore) return newEnterprises
            return [...prevEnterprises, ...newEnterprises]
          })

          setCursor((prev) =>
            isLoadMore ? prev + newEnterprises.length : newEnterprises.length
          )
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('企業検索エラー:', error)
          if (!isLoadMore) {
            setEnterprises([])
            setTotalCount(0)
            setCursor(0)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, currentSort, cursor, apiClient]
  )

  useEffect(() => {
    search(initialSearchParams)
  }, [])

  useEffect(() => {
    // ソートが変更された時は常に検索を実行（空の検索条件でも実行）
    search(currentSearchParams, false)
  }, [currentSort])

  const searchEnterprises = useCallback(
    (params: SearchParams) => {
      setCurrentSearchParams(params)
      search(params, false)
    },
    [search]
  )

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    search(currentSearchParams, true)
  }, [isLoadingMore, hasMore, isLoading, search, currentSearchParams])

  const changeSort = useCallback(
    (newSort: EnterpriseSortTypeValue) => {
      if (newSort !== currentSort) {
        setCurrentSort(newSort)
        setEnterprises([])
        setTotalCount(0)
        setCursor(0)
      }
    },
    [currentSort]
  )

  return {
    enterprises,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    cursor,
    currentSort,
    searchEnterprises,
    loadMore,
    changeSort,
  }
}
